import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import AnnouncementBar from './AnnouncementBar';
 
const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'Menu', path: '/menu' },
  { name: 'Order Online', path: '/order' },
  { name: 'Reserve', path: '/reserve' },
  { name: 'About', path: '/about' },
  { name: 'Gallery', path: '/gallery' },
  { name: 'Contact', path: '/contact' },
];
 
const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
 
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
 
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);
 
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isMobileMenuOpen]);
 
  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled ? 'bg-[#0D0D0D]/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
        }`}
        data-testid="header-container"
      >
        <AnnouncementBar />
        <nav data-testid="navbar">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link
                to="/"
                className="flex items-center"
                data-testid="logo-link"
              >
                <div className="flex flex-col leading-tight">
                  <span className="font-serif text-2xl sm:text-3xl text-[#C9A84C] tracking-wide">
                    It's Lahore
                  </span>
                  <span className="text-[10px] tracking-[0.2em] uppercase text-[#9A8F7E] font-light">
                    Authentic Pakistani Cuisine · Est. 2012
                  </span>
                </div>
              </Link>
 
              <div className="hidden lg:flex items-center space-x-8">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    data-testid={`nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                    className={`nav-link text-sm font-medium tracking-wide transition-colors ${
                      location.pathname === link.path
                        ? 'text-[#C9A84C]'
                        : 'text-[#F5F0E8] hover:text-[#C9A84C]'
                    }`}
                  >
                    {link.name}
                  </Link>
                ))}
              </div>
 
              <button
                data-testid="mobile-menu-button"
                className="lg:hidden p-2 text-[#F5F0E8] hover:text-[#C9A84C] transition-colors"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
              </button>
            </div>
          </div>
        </nav>
      </header>
 
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 lg:hidden"
            style={{ zIndex: 9999 }}
            data-testid="mobile-menu"
          >
            <div
              className="absolute inset-0 bg-[#0D0D0D]/98 backdrop-blur-xl"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.3 }}
              className="absolute right-0 top-0 h-full w-full sm:w-80 bg-[#1A2E1F] shadow-2xl"
            >
              <div className="flex flex-col h-full pt-24 px-8">
                <div className="flex-1 space-y-2">
                  {navLinks.map((link, index) => (
                    <motion.div
                      key={link.path}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <Link
                        to={link.path}
                        data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                        className={`block py-4 text-xl font-serif tracking-wide border-b border-[#2D5A3D]/30 transition-colors ${
                          location.pathname === link.path
                            ? 'text-[#C9A84C]'
                            : 'text-[#F5F0E8] hover:text-[#C9A84C]'
                        }`}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {link.name}
                      </Link>
                    </motion.div>
                  ))}
                </div>
                <div className="py-8 border-t border-[#2D5A3D]/30">
                  <p className="text-sm text-[#E8DCC8]/70">
                    44 Station Rd, Solihull, B91 3RX
                  </p>
                  <p className="text-sm text-[#C9A84C] mt-1">
                    Mon–Thu & Sat–Sun: 11am – 11pm
                  </p>
                  <p className="text-sm text-[#C9A84C] mt-1">
                    Friday: 2pm – 11pm
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
 
export default Navbar;
